import { UiKit } from '@datavant/uikit';
import { DigitalOnboardingStatus } from '../../../../../generated/tenant-api';
import { DisplayStep } from '../../AutofulfillStepper';
import { NextRouter } from 'next/router';
import { EMRConfigData, ModalControls } from '../RRAOnboardingTracker';
import { UserWithPermissions } from '../../../../../lib/user';
import { openSCConnector } from '../../helpers';
import { Stack, Tooltip } from '@datavant/dart';

const qcStep = (onboardingStatus: DigitalOnboardingStatus, router: any, addressFeedbackComplete: boolean) => {
    const QC_IN_PROGRESS_STATUSES = new Set<DigitalOnboardingStatus>([
        DigitalOnboardingStatus.ENCOUNTER_FEEDBACK_COMPLETED,
        DigitalOnboardingStatus.QC_STARTED,
        DigitalOnboardingStatus.QC_FAILED,
    ]);

    return {
        title: 'Quality Check',
        statuses: [
            ...(addressFeedbackComplete === true
                ? [DigitalOnboardingStatus.ENCOUNTER_FEEDBACK_COMPLETED]
                : []),
            DigitalOnboardingStatus.QC_STARTED,
            DigitalOnboardingStatus.QC_READY,
            DigitalOnboardingStatus.QC_COMPLETED,
            DigitalOnboardingStatus.QC_FAILED,
        ],
        tooltip: QC_IN_PROGRESS_STATUSES.has(onboardingStatus)
            ? 'Quality Control testing results are being prepared for your review. Once ready, please prioritize this step to avoid delays in Go Live.'
            : undefined,
        onClick:
            onboardingStatus === DigitalOnboardingStatus.QC_READY
                ? () => router.push('/autofulfill/quality-check')
                : undefined,
    };
};

export const SelfServeSCCloudTrackerSteps = (props: {
    isSideNavOpen: boolean;
    configData: EMRConfigData;
    currentUser: UserWithPermissions;
    router: NextRouter;
    modalControls: ModalControls;
}): DisplayStep[] => {
    const onboardingStatus = props.configData.onboardingStatus;
    const addressFeedbackComplete = props.configData.addressFeedbackComplete ?? false;

    const selfServeSteps: DisplayStep[] = [
        {
            title: 'Complete Agreement',
            onClick: () => props.modalControls.setIsAgreementModalOpen(true),
            statuses: [DigitalOnboardingStatus.EMR_SUBMITTED],
            pendoId: 'selfServeSignAgreementViaDocuSign',
        },
        {
            title: 'Set Up Connector',
            subSteps: [
                {
                    item: 'Set Up Connector',
                    itemComplete:
                        onboardingStatus === DigitalOnboardingStatus.CREDENTIALS_SUBMITTED ||
                        onboardingStatus === DigitalOnboardingStatus.CREDENTIALS_TEST_SUCCESSFUL ||
                        onboardingStatus === DigitalOnboardingStatus.BACKLOAD_COMPLETED ||
                        onboardingStatus === DigitalOnboardingStatus.ENCOUNTER_FEEDBACK_COMPLETED,
                    onClick: () => openSCConnector(props.configData),
                    tags: [
                        {
                            component: <UiKit.Tag.Warning showIcon={false} isSmall text="Pending Review" />,
                            isActive: onboardingStatus === DigitalOnboardingStatus.CREDENTIALS_SUBMITTED,
                        },
                        {
                            component: (
                                <Tooltip
                                    label="The first submission was unsuccessful. Please resubmit using a new, unique set of credentials not currently used by your practice."
                                    position="top"
                                    multiline
                                    w={220}
                                    withArrow
                                    withinPortal
                                    zIndex={10000}
                                >
                                    <Stack>
                                        <UiKit.Tag.Error showIcon={false} isSmall text="Failed Review" />
                                    </Stack>
                                </Tooltip>
                            ),
                            isActive: onboardingStatus === DigitalOnboardingStatus.CREDENTIALS_TEST_FAILED,
                        },
                    ],
                    pendoId: 'selfServeSetUpConnector',
                },
                {
                    item: 'Site Addresses',
                    itemComplete: addressFeedbackComplete,
                    onClick: () => {
                        if (!addressFeedbackComplete) {
                            props.modalControls.setIsAddressConfirmationModalOpen(true);
                        }
                    },
                    pendoId: 'selfServeSiteAddress',
                },
                {
                    item: 'Encounter Type Filters',
                    itemComplete: onboardingStatus === DigitalOnboardingStatus.ENCOUNTER_FEEDBACK_COMPLETED,
                    tags: [
                        {
                            component: <UiKit.Tag.Warning showIcon={false} isSmall text="Pending Load" />,
                            isActive:
                                onboardingStatus === DigitalOnboardingStatus.CREDENTIALS_TEST_SUCCESSFUL,
                        },
                    ],
                    onClick:
                        onboardingStatus === DigitalOnboardingStatus.BACKLOAD_COMPLETED
                            ? () => props.modalControls.setIsEncounterFilterModalOpen(true)
                            : undefined, // Dont show action arrow if backload is not completed
                    pendoId: 'selfServeEncounterTypeFilters',
                },
            ],
            statuses: [
                DigitalOnboardingStatus.DOCUSIGN_COMPLETED,
                DigitalOnboardingStatus.CREDENTIALS_SUBMITTED,
                DigitalOnboardingStatus.CREDENTIALS_TEST_FAILED,
                DigitalOnboardingStatus.CREDENTIALS_TEST_SUCCESSFUL,
                DigitalOnboardingStatus.BACKLOAD_COMPLETED,
                ...(!addressFeedbackComplete ? [DigitalOnboardingStatus.ENCOUNTER_FEEDBACK_COMPLETED] : []),
            ],
        },
        qcStep(onboardingStatus, props.router, addressFeedbackComplete),
        {
            title: 'Go Live',
            statuses: [],
        },
    ];

    return selfServeSteps;
};
